import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  AUTH_COOKIE_KEYS,
  clearAuthSessionStorage,
  getAuthCookie,
  loadAuthValue,
  removeAuthCookie,
  setAuthCookie
} from '@/utils/authSession'

interface User {
  customerId?: number | string;
  id?: number | string;
  email?: string;
  headPic?: string;
  name?: string;
  nickName?: string;
  phone?: string;
  phoneCode?: string;
  studentLists?: Student[];
  [key: string]: unknown;
}

interface Student {
  id: number;
  name: string;
  headPic: string;
  sex: number;
  invitationCode: string | null;
  countryId: number;
}


interface AuthState {
  uid: number | string | null;
  stuId: number | string | null;
  token: string | null;
  userInfo: User | null;
  studentLists: Student[];
}

const initialState: AuthState = {
  token: null,
  stuId: null,
  uid: null,
  userInfo: null,
  studentLists: []
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string | null>) => {
      state.token = action.payload;
      if (action.payload) {
        setAuthCookie(AUTH_COOKIE_KEYS.token, action.payload)
      } else {
        removeAuthCookie(AUTH_COOKIE_KEYS.token)
      }
    },
    loadToken: (state) => {
      state.token = loadAuthValue(AUTH_COOKIE_KEYS.token, 'token') || null
    },

    setStuId: (state, action: PayloadAction<string | number | null>) => {
      state.stuId = action.payload;
      if (action.payload) {
        setAuthCookie(AUTH_COOKIE_KEYS.stuId, action.payload)
      } else {
        removeAuthCookie(AUTH_COOKIE_KEYS.stuId)
      }
    },
    loadStuId: (state) => {
      state.stuId = loadAuthValue(AUTH_COOKIE_KEYS.stuId, 'stuId') || null
    },

    setUid: (state, action: PayloadAction<string | number | null>) => {
      state.uid = action.payload;
      if (action.payload) {
        setAuthCookie(AUTH_COOKIE_KEYS.uid, action.payload)
      } else {
        removeAuthCookie(AUTH_COOKIE_KEYS.uid)
      }
    },
    loadUid: (state) => {
      state.uid = loadAuthValue(AUTH_COOKIE_KEYS.uid, 'uid') || null
    },


    setUserInfo: (state, action: PayloadAction<User | null>) => {
      state.userInfo = action.payload;
      if (action.payload) {
        setAuthCookie(AUTH_COOKIE_KEYS.userInfo, JSON.stringify({
          ...action.payload,
          studentLists: state.studentLists
        }))
      } else {
        removeAuthCookie(AUTH_COOKIE_KEYS.userInfo)
      }
    },
    updateUserInfo: (state, action: PayloadAction<Partial<User>>) => {
      if (!state.userInfo) return

      state.userInfo = {
        ...state.userInfo,
        ...action.payload
      }
      setAuthCookie(AUTH_COOKIE_KEYS.userInfo, JSON.stringify({
        ...state.userInfo,
        studentLists: state.studentLists
      }))
    },
    loadUserInfo: (state) => {
      const value = getAuthCookie(AUTH_COOKIE_KEYS.userInfo)
      if (!value) return

      try {
        const userInfo = JSON.parse(value)
        state.userInfo = userInfo
        state.studentLists = Array.isArray(userInfo.studentLists) ? userInfo.studentLists : []
      } catch {
        removeAuthCookie(AUTH_COOKIE_KEYS.userInfo)
      }
    },
    setStudentLists: (state, action: PayloadAction<Student[]>) => {
      state.studentLists = action.payload;
      if (state.userInfo) {
        setAuthCookie(AUTH_COOKIE_KEYS.userInfo, JSON.stringify({
          ...state.userInfo,
          studentLists: action.payload
        }))
      }
    },
    clearSession: (state) => {
      state.token = null
      state.stuId = null
      state.uid = null
      state.userInfo = null
      state.studentLists = []
      clearAuthSessionStorage()
    },
  },
});

export const { setToken, loadToken, setUserInfo, setStuId,
  loadStuId,
  setUid,
  loadUid, loadUserInfo, updateUserInfo, setStudentLists, clearSession } = authSlice.actions;

export default authSlice.reducer;
